import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Collapse,
  FormControlLabel,
  Switch,
  Link,
  IconButton,
  Slide,
} from '@mui/material';
import { Cookie, Settings, Close, CheckCircle } from '@mui/icons-material';

// Cookie consent storage key
const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_CONSENT_VERSION = '1.0';

interface CookiePreferences {
  necessary: boolean; // Always true, cannot be disabled
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  version: string;
  timestamp: string;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  version: COOKIE_CONSENT_VERSION,
  timestamp: '',
};

interface CookieConsentProps {
  onAccept?: (preferences: CookiePreferences) => void;
  privacyPolicyUrl?: string;
  cookiePolicyUrl?: string;
}

export const CookieConsent: React.FC<CookieConsentProps> = ({
  onAccept,
  privacyPolicyUrl = '/privacy-policy',
  cookiePolicyUrl = '/cookie-policy',
}) => {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    // Check if user has already consented
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as CookiePreferences;
        // Check if consent version matches
        if (parsed.version === COOKIE_CONSENT_VERSION) {
          setPreferences(parsed);
          return; // Don't show banner if already consented
        }
      } catch {
        // Invalid stored data, show banner
      }
    }
    // Show banner after a short delay for better UX
    const timer = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    const finalPrefs = {
      ...prefs,
      necessary: true, // Always enabled
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(finalPrefs));
    setPreferences(finalPrefs);
    setVisible(false);
    onAccept?.(finalPrefs);
  };

  const handleAcceptAll = () => {
    savePreferences({
      ...preferences,
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleAcceptNecessary = () => {
    savePreferences({
      ...preferences,
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (key === 'necessary') return; // Cannot disable necessary cookies
    setPreferences({
      ...preferences,
      [key]: event.target.checked,
    });
  };

  if (!visible) return null;

  return (
    <Slide direction="up" in={visible} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          p: { xs: 2, md: 3 },
          borderRadius: { xs: '16px 16px 0 0', md: '16px 16px 0 0' },
          maxHeight: '80vh',
          overflow: 'auto',
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Cookie sx={{ fontSize: 32, color: 'primary.main', mt: 0.5 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                We Value Your Privacy
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We use cookies to enhance your browsing experience, serve personalized content, and
                analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                Read our{' '}
                <Link href={privacyPolicyUrl} target="_blank" rel="noopener">
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link href={cookiePolicyUrl} target="_blank" rel="noopener">
                  Cookie Policy
                </Link>{' '}
                to learn more.
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={handleAcceptNecessary}
              aria-label="Close cookie banner"
            >
              <Close />
            </IconButton>
          </Box>

          {/* Settings Toggle */}
          <Collapse in={showSettings}>
            <Box
              sx={{
                my: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                Cookie Preferences
              </Typography>

              {/* Necessary Cookies */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked disabled />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Necessary Cookies (Required)
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Essential for the website to function. Cannot be disabled.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Functional Cookies */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.functional}
                      onChange={handlePreferenceChange('functional')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Functional Cookies
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Enable personalized features and remember your preferences.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Analytics Cookies */}
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.analytics}
                      onChange={handlePreferenceChange('analytics')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Analytics Cookies
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Help us understand how visitors interact with our website.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Marketing Cookies */}
              <Box sx={{ mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.marketing}
                      onChange={handlePreferenceChange('marketing')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Marketing Cookies
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Used to track visitors across websites for advertising purposes.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Box>
          </Collapse>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              justifyContent: 'flex-end',
              alignItems: { xs: 'stretch', sm: 'center' },
            }}
          >
            <Button
              variant="text"
              onClick={() => setShowSettings(!showSettings)}
              startIcon={<Settings />}
              sx={{ order: { xs: 3, sm: 1 } }}
            >
              {showSettings ? 'Hide Settings' : 'Cookie Settings'}
            </Button>

            {showSettings ? (
              <Button
                variant="contained"
                onClick={handleSavePreferences}
                startIcon={<CheckCircle />}
                sx={{ order: { xs: 1, sm: 2 } }}
              >
                Save Preferences
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleAcceptNecessary}
                  sx={{ order: { xs: 2, sm: 2 } }}
                >
                  Necessary Only
                </Button>
                <Button
                  variant="contained"
                  onClick={handleAcceptAll}
                  sx={{ order: { xs: 1, sm: 3 } }}
                >
                  Accept All
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

// Hook to check cookie consent status
export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookiePreferences | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  return {
    hasConsented: !!consent,
    preferences: consent,
    canUseAnalytics: consent?.analytics ?? false,
    canUseMarketing: consent?.marketing ?? false,
    canUseFunctional: consent?.functional ?? false,
  };
};

export default CookieConsent;
